import { getAccessToken, getIdToken } from '@/auth/tokenStore';
import { DEV_AUTH, getDevEmail, getDevSub } from '@/auth/devAuth';
import { getActiveTenantId } from '@/lib/tenant';

// In production CloudFront routes /api/* to the backend ALB, so the same-origin
// default works. Override with VITE_API_BASE_URL for other environments.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const API_V1 = `${API_BASE_URL.replace(/\/$/, '')}/v1`;

export interface ApiErrorBody {
  message?: string;
  error?: string;
  // FastAPI's own request-validation errors (and a couple of connector 422s
  // that pass pydantic's raw error list straight through) shape `detail` as
  // an array of {msg, loc, ...} objects rather than a string.
  detail?: string | Array<{ msg?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;

  constructor(status: number, message: string, body: ApiErrorBody | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function buildHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);

  if (DEV_AUTH) {
    // Local dev: identify via headers instead of a bearer token.
    headers.set('X-Dev-Email', getDevEmail());
    headers.set('X-Dev-Sub', getDevSub());
  } else {
    const token = getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    // Cognito access tokens carry no email/name; the backend reads them from
    // the ID token when it provisions the user on first login.
    const idToken = getIdToken();
    if (idToken) {
      headers.set('X-Id-Token', idToken);
    }
  }

  const tenantId = getActiveTenantId();
  if (tenantId) {
    headers.set('X-Tenant-Id', tenantId);
  }

  return headers;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serialized automatically unless it is FormData. */
  body?: unknown;
}

function detailMessage(detail: ApiErrorBody['detail']): string | undefined {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => d?.msg).filter((m): m is string => Boolean(m));
    return msgs.length > 0 ? msgs.join('; ') : undefined;
  }
  return undefined;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  const message =
    body?.message ?? body?.error ?? detailMessage(body?.detail) ?? res.statusText ?? 'Request failed';
  return new ApiError(res.status, message, body);
}

/**
 * fetch wrapper that attaches the bearer token and active-tenant header, sends
 * and parses JSON, and throws a typed ApiError on non-2xx responses.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;
  const finalHeaders = buildHeaders(headers);

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    // Let the browser set the multipart boundary.
    payload = body;
  } else if (body !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${API_V1}${path}`, { ...rest, headers: finalHeaders, body: payload });

  if (!res.ok) {
    throw await parseError(res);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

// ----- Server-Sent Events streaming helper -----

export interface SSEMessage {
  /** SSE "event:" field, defaults to "message". */
  event: string;
  /** Concatenated "data:" lines. */
  data: string;
}

function parseSSEBlock(block: string): SSEMessage | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of block.split('\n')) {
    if (line.startsWith(':')) continue; // comment / keep-alive
    const sepIndex = line.indexOf(':');
    const field = sepIndex === -1 ? line : line.slice(0, sepIndex);
    let value = sepIndex === -1 ? '' : line.slice(sepIndex + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

/**
 * POSTs a JSON body and reads a text/event-stream response, invoking onMessage
 * for each parsed SSE event. Uses the fetch streaming reader (not EventSource)
 * so that the Authorization and X-Tenant-Id headers can be attached.
 */
export async function streamSSE(
  path: string,
  body: unknown,
  onMessage: (msg: SSEMessage) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers = buildHeaders({
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  });

  const res = await fetch(`${API_V1}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    throw await parseError(res);
  }
  if (!res.body) {
    throw new ApiError(res.status, 'Streaming is not supported by this response', null);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Events are separated by a blank line. Normalize CRLF first.
      buffer = buffer.replace(/\r\n/g, '\n');
      let sepIndex: number;
      while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);
        const msg = parseSSEBlock(block);
        if (msg) onMessage(msg);
      }
    }

    // Flush any trailing event that was not terminated by a blank line.
    const tail = parseSSEBlock(buffer.replace(/\r\n/g, '\n'));
    if (tail) onMessage(tail);
  } finally {
    reader.releaseLock();
  }
}
